'use client';

// «CASOS APROBADOS» del técnico administrativo: cada caso cuya acta de reparto
// ya fue FIRMADA por el director aparece aquí para ASIGNAR el proceso — fecha de
// reparto, fecha de asignación, plazo para que el abogado resuelva, abogado
// (precargado con el elegido, modificable) y la TRAZABILIDAD: la ruta ORDENADA
// de las personas que intervendrán (por defecto Abogado → Tecnólogo de apoyo →
// Abogado sustanciador → Supervisor → Director), con
// botón ENVIAR. Al enviar, el caso queda listo para la Etapa 2 del abogado.

import { useEffect, useState } from 'react';
import {
  ArrowDown, ArrowUp, BadgeCheck, CalendarDays, Eye, Plus, RefreshCw, Route,
  Send, Trash2, UserRound,
} from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import {
  asignarProceso, listarAprobadas, listarFirmantes, obtenerSolicitud,
  type Firmante, type Solicitud,
} from '@/lib/preRadicacion';
import { esAbogadoSustanciador, esReceptorCaso } from '@/lib/roles';
import ActaRepartoDialog from '@/components/ActaRepartoDialog';
import HeroVista from '@/components/HeroVista';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const fechaCorta = (iso?: string | null) => (iso ? iso.slice(0, 10) : '—');
const soloFecha = (iso?: string | null) => (iso ? iso.slice(0, 10) : '');

// Estilo institucional de los campos del modal de asignación: delineado verde
// suave + fondo apenas teñido; el foco refuerza el verde primario.
const CAMPO =
  'rounded-lg border-primary/35 bg-primary/[0.03] focus-visible:border-primary focus-visible:ring-primary/25';
const CAMPO_ALTO = `h-11 ${CAMPO}`;

interface Paso {
  usuarioId: string;
  etiqueta: string;
}

export default function CasosAprobados() {
  const [filas, setFilas] = useState<Solicitud[]>([]);
  const [firmantes, setFirmantes] = useState<Firmante[]>([]);
  const [cargando, setCargando] = useState(true);
  const [verActa, setVerActa] = useState<Solicitud | null>(null);
  const [asignando, setAsignando] = useState<Solicitud | null>(null);

  async function cargar() {
    setCargando(true);
    try {
      setFilas(await listarAprobadas());
    } catch (e) {
      avisoError('No se pudieron cargar los expedientes aprobados', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
    listarFirmantes().then(setFirmantes).catch(() => setFirmantes([]));
  }, []);

  async function abrirAsignacion(s: Solicitud) {
    try {
      // El detalle trae la trazabilidad ya guardada (si la hay).
      setAsignando(await obtenerSolicitud(s.id));
    } catch {
      setAsignando(s);
    }
  }

  const pendientes = filas.filter((f) => !f.asignacion_enviada);
  const enviadas = filas.filter((f) => f.asignacion_enviada);

  return (
    <div className="space-y-6">
      <HeroVista
        icono={BadgeCheck}
        titulo="Expedientes aprobados"
        descripcion="Actas de reparto ya firmadas por el Director: asigne el proceso (fechas, plazo, abogado y trazabilidad) y envíelo."
        acciones={
          <Button
            variant="outline"
            className="border-white/40 bg-white/10 text-primary-foreground hover:bg-white/20"
            onClick={cargar}
          >
            <RefreshCw className="size-4" /> Actualizar
          </Button>
        }
      />

      {cargando ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Cargando…</p>
      ) : filas.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          Aún no hay expedientes con acta firmada por el Director.
        </p>
      ) : (
        <div className="space-y-4">
          {[...pendientes, ...enviadas].map((s) => (
            <div key={s.id} className="flex flex-col gap-3 rounded-2xl border bg-card p-4 shadow-sm sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-primary">
                    {s.radicado_completo ?? s.radicado_origen ?? 'Sin radicado'}
                  </p>
                  {s.asignacion_enviada ? (
                    <Badge className="bg-emerald-600">Proceso asignado y enviado</Badge>
                  ) : (
                    <Badge variant="outline" className="border-amber-400/60 bg-amber-50 text-amber-800">
                      Pendiente de asignar
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Firmada: {fechaCorta(s.acta_firmada_en)}
                  {s.abogado_nombre && <> · Abogado: {s.abogado_nombre}</>}
                  {s.sustanciador_nombre && <> · Sustanciador: {s.sustanciador_nombre}</>}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Button variant="outline" onClick={() => setVerActa(s)}>
                  <Eye className="size-4" /> Ver acta firmada
                </Button>
                <Button onClick={() => abrirAsignacion(s)}>
                  <Send className="size-4" />
                  {s.asignacion_enviada ? 'Ver / reasignar' : 'Asignar proceso'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {verActa && (
        <ActaRepartoDialog solicitud={verActa} firmantes={firmantes} onClose={() => setVerActa(null)} />
      )}

      {asignando && (
        <ModalAsignar
          solicitud={asignando}
          firmantes={firmantes}
          onClose={() => setAsignando(null)}
          onListo={() => { setAsignando(null); cargar(); }}
        />
      )}
    </div>
  );
}

// ---- Modal de asignación: fechas + plazo + abogado + TRAZABILIDAD + ENVIAR ----
function ModalAsignar({
  solicitud: s, firmantes, onClose, onListo,
}: {
  solicitud: Solicitud;
  firmantes: Firmante[];
  onClose: () => void;
  onListo: () => void;
}) {
  // Abogados a los que se puede asignar el proceso: el elegido aquí es EL QUE
  // INICIA LA ETAPA 2, así que la lista es la misma que gatea esos permisos.
  const abogados = firmantes.filter((f) => esReceptorCaso(f.rol));

  const [fechaReparto, setFechaReparto] = useState(soloFecha(s.fecha_reparto) || soloFecha(s.acta_firmada_en));
  const [fechaAsignacion, setFechaAsignacion] = useState(soloFecha(s.fecha_asignacion));
  const [plazo, setPlazo] = useState(soloFecha(s.plazo_abogado_fecha));
  const [abogadoId, setAbogadoId] = useState(s.abogado_id ?? '');
  const [pasos, setPasos] = useState<Paso[]>(() => trazabilidadInicial(s, firmantes));
  const [enviando, setEnviando] = useState(false);

  const nombreDe = (id: string) => firmantes.find((f) => f.id === id)?.nombre ?? '';

  function mover(i: number, delta: number) {
    setPasos((p) => {
      const j = i + delta;
      if (j < 0 || j >= p.length) return p;
      const copia = [...p];
      [copia[i], copia[j]] = [copia[j], copia[i]];
      return copia;
    });
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!fechaReparto || !fechaAsignacion || !plazo) {
      avisoError('Diligencie la fecha de reparto, la fecha de asignación y el plazo.');
      return;
    }
    const ruta = pasos.filter((p) => p.usuarioId);
    if (ruta.length === 0) {
      avisoError('Agregue al menos una persona a la trazabilidad.');
      return;
    }
    setEnviando(true);
    try {
      await asignarProceso(s.id, {
        fechaReparto,
        fechaAsignacion,
        plazoAbogadoFecha: plazo,
        abogadoId: abogadoId || undefined,
        // El tecnólogo de apoyo ya no se elige aquí (se conserva el asignado
        // en la pre-radicación, si lo hay).
        trazabilidad: ruta.map((p) => ({ usuarioId: p.usuarioId, etiqueta: p.etiqueta || undefined })),
      });
      avisoExito('Proceso asignado y ENVIADO.', 'Las personas asignadas fueron notificadas.');
      onListo();
    } catch (err) {
      avisoError('No se pudo asignar el proceso', err instanceof Error ? err.message : undefined);
      setEnviando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* OJO: sobreescribir sm:max-w-* con la MISMA variante o queda en 512px. */}
      <DialogContent className="max-h-[92vh] w-[94vw] max-w-[94vw] overflow-y-auto rounded-2xl p-6 sm:max-w-4xl sm:p-8">
        <DialogHeader className="items-center gap-1 text-center sm:text-center">
          <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Send className="size-6" />
          </span>
          <DialogTitle className="text-xl font-bold tracking-tight">Asignar proceso</DialogTitle>
          <DialogDescription>
            Expediente{' '}
            <span className="font-semibold text-primary">
              {s.radicado_completo ?? s.radicado_origen ?? ''}
            </span>{' '}
            — acta firmada por el Director.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={enviar} className="space-y-5">
          {/* Fechas y plazo */}
          <section className="space-y-4 rounded-xl border border-primary/25 bg-primary/[0.02] p-4 sm:p-5">
            <p className="flex items-center gap-2 text-sm font-semibold text-primary">
              <CalendarDays className="size-4" /> Fechas y plazo
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="freparto">Fecha de reparto</Label>
                <Input id="freparto" type="date" required className={CAMPO_ALTO}
                  value={fechaReparto} onChange={(e) => setFechaReparto(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fasign">Fecha de asignación del proceso</Label>
                <Input id="fasign" type="date" required className={CAMPO_ALTO}
                  value={fechaAsignacion} onChange={(e) => setFechaAsignacion(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fplazo">Plazo para que el abogado resuelva</Label>
                <Input id="fplazo" type="date" required className={CAMPO_ALTO}
                  value={plazo} onChange={(e) => setPlazo(e.target.value)} />
              </div>
            </div>
          </section>

          {/* Abogado que recibe el proceso */}
          <section className="space-y-4 rounded-xl border border-primary/25 bg-primary/[0.02] p-4 sm:p-5">
            <p className="flex items-center gap-2 text-sm font-semibold text-primary">
              <UserRound className="size-4" /> Abogado al que se le asigna
            </p>
            <div className="space-y-2 sm:max-w-md">
              <Select value={abogadoId} onValueChange={setAbogadoId}>
                <SelectTrigger className={`w-full data-[size=default]:h-11 ${CAMPO}`}>
                  <SelectValue placeholder="Seleccione…" />
                </SelectTrigger>
                <SelectContent>
                  {(abogados.length ? abogados : firmantes).map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Precargado con el abogado elegido en la pre-radicación; puede modificarlo.
              </p>
            </div>
          </section>

          {/* TRAZABILIDAD: ruta ordenada de intervinientes (reordenable). */}
          <section className="space-y-3 rounded-xl border border-primary/25 bg-primary/[0.02] p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-sm font-semibold text-primary">
                <Route className="size-4" /> Trazabilidad — ruta de las personas que van a intervenir
              </p>
              <Button
                type="button" size="sm" variant="outline"
                className="h-7 rounded-full border-primary/40 px-2.5 text-xs text-primary hover:bg-primary/10 hover:text-primary"
                onClick={() => setPasos((p) => [...p, { usuarioId: '', etiqueta: '' }])}
              >
                <Plus className="size-3.5" /> Agregar persona
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              El expediente irá llegando a cada persona según este orden. Por defecto: Abogado →
              Tecnólogo de apoyo → Abogado sustanciador → Supervisor → Director.
            </p>
            {pasos.length === 0 && (
              <p className="text-sm text-muted-foreground">Sin personas: agregue al menos una.</p>
            )}
            <ol className="space-y-2">
              {pasos.map((p, i) => (
                <li key={`${p.usuarioId}-${i}`} className="flex flex-wrap items-center gap-2">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {i + 1}
                  </span>
                  <div className="min-w-40 flex-1">
                    <Select
                      value={p.usuarioId}
                      onValueChange={(v) =>
                        setPasos((prev) => prev.map((x, j) => (j === i ? { ...x, usuarioId: v } : x)))}
                    >
                      <SelectTrigger className={`w-full ${CAMPO}`}>
                        <SelectValue placeholder="Seleccione la persona…" />
                      </SelectTrigger>
                      <SelectContent>
                        {firmantes.map((f) => (
                          <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    className={`w-40 ${CAMPO}`}
                    placeholder="Rol en la ruta"
                    value={p.etiqueta}
                    onChange={(e) =>
                      setPasos((prev) => prev.map((x, j) => (j === i ? { ...x, etiqueta: e.target.value } : x)))}
                  />
                  <div className="flex shrink-0 gap-1">
                    <Button type="button" size="icon" variant="ghost" className="size-7" aria-label="Subir"
                      disabled={i === 0} onClick={() => mover(i, -1)}>
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" className="size-7" aria-label="Bajar"
                      disabled={i === pasos.length - 1} onClick={() => mover(i, 1)}>
                      <ArrowDown className="size-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" className="size-7 text-destructive" aria-label="Quitar"
                      onClick={() => setPasos((prev) => prev.filter((_, j) => j !== i))}>
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                  {p.usuarioId && (
                    <span className="w-full pl-8 text-xs text-muted-foreground sm:hidden">
                      {nombreDe(p.usuarioId)}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </section>

          <DialogFooter className="gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="rounded-full px-8" disabled={enviando}>
              <Send className="size-4" /> {enviando ? 'Enviando…' : 'Enviar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Ruta por defecto (regla del cliente): Abogado (elegido, modificable) →
 * Tecnólogo de apoyo → Abogado sustanciador → Supervisor → Director. Si la
 * solicitud ya tiene trazabilidad guardada, se retoma esa.
 */
function trazabilidadInicial(s: Solicitud, firmantes: Firmante[]): Paso[] {
  if (s.trazabilidad && s.trazabilidad.length > 0) {
    return s.trazabilidad.map((p) => ({ usuarioId: p.usuario_id, etiqueta: p.etiqueta ?? '' }));
  }
  const primero = (rol: string) => firmantes.find((f) => f.rol === rol)?.id ?? '';
  // Sin sustanciador elegido, se propone el primer abogado sustanciador disponible.
  const primerSustanciador = firmantes.find((f) => esAbogadoSustanciador(f.rol))?.id ?? '';
  const candidatos: Paso[] = [
    { usuarioId: s.abogado_id ?? '', etiqueta: 'Abogado' },
    { usuarioId: s.tecnologo_apoyo_id ?? primero('TECNOLOGO_APOYO'), etiqueta: 'Tecnólogo de apoyo' },
    { usuarioId: s.sustanciador_id ?? primerSustanciador, etiqueta: 'Abogado sustanciador' },
    { usuarioId: primero('SUPERVISOR'), etiqueta: 'Supervisor' },
    { usuarioId: s.director_id ?? primero('DIRECTOR_INSTRUCCION'), etiqueta: 'Director' },
  ];
  return candidatos.filter((c) => c.usuarioId);
}
